import { useReservationAlert } from '../components/ReservationAlertContext';   // RESERVIERUNGSKETTE-001
import AdminRideCompletion from '../components/AdminRideCompletion';
import DispatchReview from '../components/DispatchReview';
import EssenCompletionReviews from '../components/EssenCompletionReviews';   // A-1: Essen-Abschlüsse ohne Streckennachweis
import ReservationReassign from '../components/ReservationReassign';
import RideReassign from '../components/RideReassign';   // ADMIN-FAHRT-ZUWEISUNG-001: Fahrer zuweisen / an anderen Fahrer übergeben
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import toast from 'react-hot-toast';
import {
  Ban,
  Banknote,
  Building2,
  CalendarDays,
  Car,
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock3,
  CreditCard,
  Filter,
  Hash,
  MapPin,
  RefreshCw,
  Route,
  Search,
  ShieldAlert,
  Star,
  User,
  Wallet,
  X,
} from 'lucide-react';
import { Empty, ErrorBox, Skeleton } from '../ui/kit';
import { readRideStops } from '../utils/rideStops';
import { useAuth } from '../App';
import './Rides.css';
import { alsDatum, datumUndZeit } from '../lib/zeit';

const SC = {
  searching: '#4b5563',
  accepted: '#586b87',
  arrived: '#7c5a91',
  in_progress: '#9c27b0',
  completed: '#4b7c58',
  cancelled: '#b84b4b',
  no_driver: '#a16207',
  price_review: '#a16207',
  price_released: '#4b5563',
};
// A-1: kein Filterstatus (Serverstatus bleibt completed), nur die Anzeige in der Zeile.
const ROW_SL = { price_review: 'Preis in Prüfung', price_released: 'Ohne Abrechnung' };

const SL = {
  scheduled: 'Reservierung',
  admin_review: 'Prüfung · angehalten',
  searching: 'Suche',
  accepted: 'Angenommen',
  arrived: 'Angekommen',
  in_progress: 'Unterwegs',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
  // RESERVIERUNG-KOMPLETT-001: kein Rohwort „no_driver“ mehr in der Übersicht.
  no_driver: 'Kein Fahrer gefunden',
};

const TL = {
  standard: 'VOOGO Basis',
  comfort: 'VOOGO Komfort',
  premium: 'VOOGO Business',
  xl: 'VOOGO Family',
  max: 'VOOGO Van',
  taxi: 'VOOGO Taxi',
};

// Deutsche Labels für den kodierten Storno-Grund (fester Code-Vertrag Kunde/Fahrer/Server).
const CODE_LABELS = {
  plans_changed: 'Meine Pläne haben sich geändert',
  wait_too_long: 'Wartezeit war zu lang',
  wrong_address: 'Falsche Adresse eingegeben',
  driver_not_approaching: 'Fahrer kam nicht näher',
  rider_unreachable: 'Fahrgast nicht erreichbar',
  pickup_route_problem: 'Problem mit der Route zum Abholort',
  unsafe_pickup: 'Kein sicherer Abholort',
  vehicle_problem: 'Problem mit dem Fahrzeug',
  customer_cancelled: 'Vom Kunden storniert',
  no_show: 'Fahrgast nicht erschienen',
  other: 'Anderer Grund',
};

const CANCELLED_BY_LABELS = { customer: 'Kunde', driver: 'Fahrer', admin: 'Admin' };

const RETURN_REASON_LABELS = {
  driver_return: 'Grund nicht übermittelt',
  rider_unreachable: 'Fahrgast nicht erreichbar',
  pickup_route_problem: 'Problem mit der Route zum Abholort',
  unsafe_pickup: 'Kein sicherer Abholort',
  vehicle_problem: 'Problem mit dem Fahrzeug',
  passenger_no_show: 'Fahrgast nicht erschienen',
  too_far: 'Abholort zu weit entfernt',
  other: 'Anderer Grund',
};

const ACCEPTANCE_MODE_LABELS = {
  manual: 'von Hand',
  automatic_foreground: 'automatisch im Vordergrund',
  automatic_assigned: 'automatisch zugeteilt',
  automatic_background: 'automatisch im Hintergrund',
  admin: 'vom Admin',
  reservation: 'Reservierung angenommen',
  unknown: 'Art historisch nicht übermittelt',
};

const money = (value) => Number(value || 0).toLocaleString('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const dateTime = (value, withYear = false) => datumUndZeit(value, { jahr: withYear ? 'kurz' : false });
// RESERVIERUNGSKETTE-001: Strecke lesbar (5,4 statt 5.3910420000000006).
const formatKm = (value) => (value == null || !Number.isFinite(Number(value)) ? '—'
  : Number(value).toLocaleString('de-DE', { maximumFractionDigits: 1 }));

const legacyTimeline = (ride, driverReturns) => [
  { key: 'created', type: 'created', occurred_at: ride.created_at },
  ...driverReturns.map((entry, index) => ({
    key: `legacy-return-${index}`,
    type: 'returned',
    occurred_at: entry.released_at,
    driver_name: entry.driver_name,
    reason_code: entry.reason_code,
    return_outcome: entry.return_outcome,
  })),
  { key: 'arrived', type: 'arrived', occurred_at: ride.arrived_at },
  { key: 'started', type: 'started', occurred_at: ride.started_at },
  { key: 'completed', type: 'completed', occurred_at: ride.completed_at },
].filter((entry) => entry.occurred_at).sort((left, right) => (
  (alsDatum(left.occurred_at)?.getTime() || 0) - (alsDatum(right.occurred_at)?.getTime() || 0)
));

function timelineLabel(entry) {
  if (entry.type === 'created') return 'Erstellt';
  if (entry.type === 'accepted') {
    const driver = entry.driver_name || 'Unbekannter Fahrer';
    const mode = ACCEPTANCE_MODE_LABELS[entry.acceptance_mode] || ACCEPTANCE_MODE_LABELS.unknown;
    return `Angenommen von ${driver} (${mode})`;
  }
  if (entry.type === 'returned') {
    const driver = entry.driver_name || 'Unbekannter Fahrer';
    const reason = RETURN_REASON_LABELS[entry.reason_code]
      || `Unbekannter gespeicherter Grund (${entry.reason_code || 'ohne Code'})`;
    return `Zurückgegeben von ${driver} · ${reason}${entry.return_outcome === 'admin_review' ? ' · zur GPS-Prüfung angehalten' : ''}`;
  }
  if (entry.type === 'arrived') return 'Beim Fahrgast angekommen';
  if (entry.type === 'started') return 'Gestartet';
  if (entry.type === 'completed') return 'Abgeschlossen';
  if (entry.type === 'cancelled') {
    const actor = CANCELLED_BY_LABELS[entry.cancelled_by] || 'Unbekannt';
    const reason = CODE_LABELS[entry.reason_code] || entry.reason_text
      || `Unbekannter gespeicherter Grund (${entry.reason_code || 'ohne Code'})`;
    return `Storniert von ${actor} · ${reason}`;
  }
  return 'Unbekanntes Ereignis';
}

// Beide Admin-Aufrufer verwenden exakt denselben Detailvertrag. Das serverseitige timeline_events-Feld
// bleibt dabei unverändert; driver_returns bleibt ausschließlich der bestehende Storno-Kasten-Vertrag.
export const rideDetailFromResponse = (data) => ({
  ...data.ride,
  driver_returns: data.driver_returns || [],
  dispatch_reviews: data.dispatch_reviews || [],
});

function StatusPill({ status }) {
  return (
    <span className="rides-status" style={{ '--ride-status': SC[status] || '#4b5563' }}>
      <i /> {SL[status] || ROW_SL[status] || status || 'Unbekannt'}
    </span>
  );
}

function TypePill({ type }) {
  return <span className="rides-type">{TL[type] || type || '—'}</span>;
}

function DetailLine({ label, value, strong = false }) {
  return (
    <div className="ride-detail-line">
      <span>{label}</span>
      <strong className={strong ? 'is-strong' : ''}>{value}</strong>
    </div>
  );
}

// KORREKTURBUCHUNG-ADMIN-001: Buchungszeilen der Fahrt aus dem unveränderlichen Finanzbuch,
// mit Gegenbuchung (Weg A) und freier Korrektur (Weg B). Sichtbar nur mit finance.manage —
// der Server erzwingt dasselbe Recht auf jeder Route.
// REVIEW-FIX R3-P3: Betrag STRIKT parsen — höchstens zwei Nachkommastellen, sonst ablehnen.
// Vorher wurde 1,005 still zu 100 Cent gerundet; stilles Runden ist bei Geld ein Fehlerbild.
const parseEuroCents = (raw) => {
  const value = String(raw).trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return null;
  const cents = Math.round(Number(value) * 100);
  return cents > 0 ? cents : null;
};

// Idempotenz-Schlüssel für Korrekturbuchungen: crypto.randomUUID mit Fallback
// (ältere Testumgebungen haben kein globales crypto).
const newCorrectionKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `korrektur-${Date.now()}-${Math.random().toString(16).slice(2)}`);

// Der Scope trennt angemeldeten Admin und Ziel-Fahrt, ohne deren IDs lesbar in
// sessionStorage abzulegen. Persistiert wird ausschließlich der Idempotency-Key.
const opaqueRideCorrectionScope = (...parts) => {
  const input = parts.map(part => `${String(part).length}:${String(part)}`).join('|');
  let left = 0x811c9dc5;
  let right = 0x9e3779b9;
  for (let index = 0; index < input.length; index += 1) {
    const code = input.charCodeAt(index);
    left = Math.imul(left ^ code, 0x01000193);
    right = Math.imul(right ^ code, 0x85ebca6b);
    right ^= right >>> 13;
  }
  return `${(left >>> 0).toString(36)}-${(right >>> 0).toString(36)}`;
};

const rideCorrectionStorageName = (adminId, rideId) => (
  adminId && rideId
    ? `voogo.admin.finance-correction.v1:ride:${opaqueRideCorrectionScope(adminId, rideId)}`
    : null
);

const storedRideCorrectionKey = (storageName) => {
  if (storageName) {
    try {
      const stored = sessionStorage.getItem(storageName);
      if (stored) return stored;
    } catch {}
  }
  const created = newCorrectionKey();
  if (storageName) {
    try { sessionStorage.setItem(storageName, created); } catch {}
  }
  return created;
};

const clearStoredRideCorrectionKey = (storageName, key) => {
  if (!storageName) return;
  try {
    if (sessionStorage.getItem(storageName) === key) sessionStorage.removeItem(storageName);
  } catch {}
};

const rideCorrectionResultIsUnclear = (requestError) => {
  const status = requestError?.response?.status;
  return !requestError?.response || !Number.isFinite(status) || status === 408 || status >= 500;
};

const LEDGER_TYPE_LABELS = {
  completed: 'Fahrtabschluss',
  cancellation_fee: 'Stornogebühr',
  no_show_fee: 'No-Show-Gebühr',
  tip: 'Trinkgeld',
  reversal: 'Gegenbuchung',
  bonus_info: 'Bonus-Info',
  manual_correction: 'Korrektur',
  opening_balance: 'Eröffnungsstand',
};
const REVERSIBLE_TYPES = ['completed', 'cancellation_fee', 'no_show_fee', 'tip', 'manual_correction'];

function RideLedgerSection({ rideId }) {
  const { can, user } = useAuth();
  const allowed = Boolean(can && can('finance.manage'));
  const [entries, setEntries] = useState(null);
  // REVIEW-FIX R5-P3: fail-closed — Geldaktionen erscheinen NUR bei ausdrücklichem true
  // (fehlendes, nulles oder falsch typisiertes Feld sperrt die Anzeige; der Server sperrt
  // die Buchung ohnehin).
  const [settleable, setSettleable] = useState(false);
  // TESTFAHRTEN-AUSBUCHEN-001: Nachweis der Ausbuchung (null = nicht ausgebucht).
  const [testWriteoff, setTestWriteoff] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState('abzug');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const storageNameRef = useRef(null);
  const idempotencyRef = useRef(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!allowed) return;
    // REVIEW-FIX P2-4: ein Ladefehler ist KEIN leeres Buch — sonst verbirgt die Akte
    // vorhandene Buchungen. Fehler bekommt einen eigenen Zustand mit erneutem Versuch.
    api.get(`/admin/rides/${rideId}/ledger`)
      .then((response) => {
        setEntries(response.data.entries || []);
        setSettleable(response.data.partner_settleable === true);
        setTestWriteoff(response.data.test_writeoff || null);
      })
      .catch(() => setEntries('error'));
  }, [rideId, reloadToken, allowed]);

  if (!allowed) return null;

  const driverId = (Array.isArray(entries) && entries.find((entry) => entry.driver_id)?.driver_id) || null;

  const reverse = async (entryId) => {
    const input = window.prompt('Grund der Gegenbuchung (Pflicht) — die ganze Buchungszeile wird gegen gebucht:');
    if (input === null) return;
    if (!input.trim()) { toast.error('Grund ist Pflicht'); return; }
    try {
      await api.post(`/admin/ledger/${entryId}/reverse`, { reason: input.trim() });
      toast.success('Buchungszeile gegen gebucht');
      setReloadToken((value) => value + 1);
    } catch (requestError) {
      toast.error(requestError.response?.data?.message || 'Gegenbuchung abgelehnt');
    }
  };

  // TESTFAHRTEN-AUSBUCHEN-001 (Owner-Entscheide 14.09.2026): Fahrt als Testfahrt ausbuchen — Pflichtgrund,
  // unveränderlicher Nachweis, Storno-Beleg je Original; liegt eine Buchungszeile schon in einer Periode,
  // meldet der Server 409 und die Entscheidung wird hier SICHTBAR bestätigt (nie still).
  const writeOffTestRide = async () => {
    const input = window.prompt('Grund der Ausbuchung als Testfahrt (Pflicht) — die Fahrt verschwindet aus Fahrer-, Partner- und Admin-Statistik, Auftragsbuch und Preis-Lernen; Buch und Belege bleiben, je Beleg entsteht ein Storno-Beleg:');
    if (input === null) return;
    if (!input.trim()) { toast.error('Grund ist Pflicht'); return; }
    const post = (acknowledge) => api.post(`/admin/rides/${rideId}/test-writeoff`, { reason: input.trim(), acknowledge_settled: acknowledge });
    try {
      await post(false);
      toast.success('Fahrt als Testfahrt ausgebucht');
      setReloadToken((value) => value + 1);
    } catch (requestError) {
      const data = requestError.response?.data;
      if (requestError.response?.status === 409 && data?.error === 'ride_in_period') {
        const settled = Array.isArray(data.settled_entries) ? data.settled_entries.length : 0;
        if (!window.confirm(`${settled} Buchungszeile(n) dieser Fahrt liegen bereits in einer berechneten oder bezahlten Periode. Sie bleiben dort sichtbar; eine Korrektur muss separat gebucht werden. Trotzdem als Testfahrt ausbuchen?`)) return;
        try {
          await post(true);
          toast.success('Fahrt als Testfahrt ausgebucht — abgerechnete Buchungszeilen bleiben in ihrer Periode');
          setReloadToken((value) => value + 1);
        } catch (secondError) {
          toast.error(secondError.response?.data?.message || 'Ausbuchen abgelehnt');
        }
        return;
      }
      toast.error(data?.message || 'Ausbuchen abgelehnt');
    }
  };

  const submitCorrection = async (event) => {
    event.preventDefault();
    // Der State-Render kommt erst nach dem Event; das Ref sperrt synchrone Doppel-Submits.
    if (inFlightRef.current) return;
    const parsed = parseEuroCents(amount);
    if (parsed === null) { toast.error('Betrag in Euro mit höchstens zwei Nachkommastellen angeben, z. B. 4,00'); return; }
    if (!reason.trim()) { toast.error('Grund ist Pflicht'); return; }
    if (!driverId) { toast.error('Keine Fahrer-Zuordnung in den Buchungszeilen'); return; }

    const storageName = rideCorrectionStorageName(user?.id, rideId);
    if (storageNameRef.current !== storageName) {
      storageNameRef.current = storageName;
      idempotencyRef.current = null;
    }
    const requestKey = idempotencyRef.current || storedRideCorrectionKey(storageName);
    idempotencyRef.current = requestKey;
    inFlightRef.current = true;
    setSaving(true);
    try {
      await api.post('/admin/finance/corrections', {
        driver_id: driverId,
        ride_id: rideId,
        amount_cents: direction === 'abzug' ? -parsed : parsed,
        reason: reason.trim(),
      }, { headers: { 'Idempotency-Key': requestKey } });
      toast.success(direction === 'abzug' ? 'Abzug gebucht' : 'Gutschrift gebucht');
      clearStoredRideCorrectionKey(storageName, requestKey);
      if (idempotencyRef.current === requestKey) idempotencyRef.current = null;
      setAmount('');
      setReason('');
      setReloadToken((value) => value + 1);
    } catch (requestError) {
      if (rideCorrectionResultIsUnclear(requestError)) {
        toast.error('Ergebnis unklar – die Buchung wurde nicht sicher bestätigt. Bitte erneut senden; derselbe Vorgangsschlüssel wird wiederverwendet.');
      } else {
        toast.error(requestError.response?.data?.message || 'Korrektur abgelehnt');
      }
    } finally {
      inFlightRef.current = false;
      setSaving(false);
    }
  };

  return (
    <section className="ride-ledger-book" aria-label="Buchungszeilen und Korrekturen">
      <header>
        <span><Wallet size={16} /></span>
        <div>
          <small>Unveränderliches Finanzbuch</small>
          <h3>Buchungszeilen &amp; Korrekturen</h3>
        </div>
      </header>

      {entries !== null && entries !== 'error' && (
        <div className="ride-test-writeoff">
          {testWriteoff ? (
            <p className="ride-test-writeoff-note"><strong>Als Testfahrt ausgebucht</strong> am {dateTime(testWriteoff.created_at, true)} · Grund: {testWriteoff.reason}</p>
          ) : (
            <button type="button" className="ride-test-writeoff-button" onClick={writeOffTestRide}>Als Testfahrt ausbuchen</button>
          )}
        </div>
      )}

      {entries === null ? (
        <p className="ride-ledger-empty">Buchungszeilen werden geladen …</p>
      ) : entries === 'error' ? (
        <ErrorBox text="Buchungszeilen konnten nicht geladen werden." onRetry={() => setReloadToken((value) => value + 1)} />
      ) : entries.length === 0 ? (
        <p className="ride-ledger-empty">Noch keine Buchungszeilen — Korrekturen sind erst möglich, sobald die Fahrt beendet und gebucht ist.</p>
      ) : (
        <>
          <ul className="ride-ledger-list">
            {entries.map((entry) => (
              <li key={entry.id}>
                <div className="ride-ledger-line-main">
                  <strong>{LEDGER_TYPE_LABELS[entry.event_type] || entry.event_type}</strong>
                  <span className="ride-ledger-date">{dateTime(entry.occurred_at, true)}</span>
                  <strong className={entry.amount < 0 ? 'is-negative' : ''}>{money(entry.amount)} €</strong>
                </div>
                {entry.reason && <p className="ride-ledger-reason">{entry.reason}</p>}
                <div className="ride-ledger-line-actions">
                  {entry.reversed_by && <em className="ride-ledger-flag">Gegen gebucht</em>}
                  {entry.in_paid_period && <em className="ride-ledger-flag">In bezahlter Periode</em>}
                  {entry.written_off && <em className="ride-ledger-flag">Ausgebucht (Testfahrt)</em>}
                  {entry.partner_settleable === true && !entry.reversed_by && !entry.written_off && REVERSIBLE_TYPES.includes(entry.event_type) && (
                    <button type="button" onClick={() => reverse(entry.id)}>Gegenbuchen</button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {!settleable && (
            <p className="ride-ledger-locked">Der Fahrt-Partner ist nicht regulär abrechenbar oder fehlt — die freie Korrektur ist hier gesperrt. Gegenbuchungen richten sich nach dem Partner der jeweiligen Buchungszeile.</p>
          )}
          {settleable && (
          <form className="ride-ledger-correction" onSubmit={submitCorrection}>
            <div>
              <strong>Korrektur buchen</strong>
              <small>Wirkt als eigene Buchungszeile auf die nächste Partner-Auszahlung. Das Backend liefert dem Fahrer Fahrtumsatz, Korrektur und korrigierten Gesamtwert getrennt; der Partner sieht die Korrektursumme in Portal und Abrechnung. Der Grund bleibt im Finanz-Audit.</small>
            </div>
            <div className="ride-ledger-correction-fields">
              <select value={direction} onChange={(event) => setDirection(event.target.value)} aria-label="Richtung">
                <option value="abzug">Abziehen (−)</option>
                <option value="gutschrift">Gutschreiben (+)</option>
              </select>
              <input
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="Betrag in €, z. B. 4,00"
                inputMode="decimal"
                aria-label="Betrag in Euro"
              />
              <input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Grund (Pflicht), z. B. Reklamierte Wartezeit"
                aria-label="Grund"
              />
              <button type="submit" disabled={saving}>{saving ? 'Bucht…' : 'Buchen'}</button>
            </div>
          </form>
          )}
        </>
      )}
    </section>
  );
}

// ADMIN-DASHBOARD-FAHRT-DETAIL-001: auch das Dashboard („Letzte Fahrten") öffnet dieses Detail.
export function RideDetailModal({ ride, onClose, onCancel, onCompleted, onReassigned }) {
  if (!ride) return null;
  const { stops, unreadable } = readRideStops(ride.stops);
  const driverReturns = Array.isArray(ride.driver_returns) ? ride.driver_returns : [];
  const timelineEvents = Array.isArray(ride.timeline_events)
    ? ride.timeline_events
    : legacyTimeline(ride, driverReturns);
  const cancelReason = ride.no_show === 1
    ? CODE_LABELS.no_show
    : (CODE_LABELS[ride.cancel_reason_code] || ride.cancel_reason || '—');

  return (
    <div className="ride-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="ride-detail-title">
      <article className="ride-modal">
        <header className="ride-modal-head">
          <div>
            <span className="rides-eyebrow"><Route size={13} /> VOOGO Ride Operations</span>
            <div className="ride-modal-title-row">
              <h2 id="ride-detail-title">Fahrt-Detail</h2>
              <StatusPill status={ride.dispatch_reviews?.some(review => review.state === 'open') ? 'admin_review' : ride.status} />
              {ride.ride_type && <TypePill type={ride.ride_type} />}
              {ride.test_writeoff_id && <span className="ride-test-writeoff-pill">Testfahrt · ausgebucht</span>}
            </div>
            {(ride.scheduled_for || ride.reservation_planned_for) && (
              <p className="ride-reservation-term" data-reservation-term>
                <Clock3 size={12} /> Reservierung für {dateTime(ride.scheduled_for || ride.reservation_planned_for, true)} Uhr
                {!ride.scheduled_for && ride.reservation_planned_for ? ' · wird live vermittelt' : ''}
              </p>
            )}
            <p><Hash size={11} /> {ride.id}</p>
          </div>
          <button type="button" className="ride-modal-close" onClick={onClose} aria-label="Fahrt-Detail schließen">
            <X size={16} />
          </button>
        </header>

        <div className="ride-modal-content">
          <DispatchReview key={ride.id} rideId={ride.id} reviews={ride.dispatch_reviews} onChanged={onReassigned} />
          <section className="ride-people-grid">
            <article className="ride-detail-card">
              <header><span><User size={16} /></span><div><small>Fahrgast</small><h3>Kunde</h3></div></header>
              {/* AKTE-LINKS-FAHRT-DETAIL-001: Name öffnet die Kundenakte (/users?detail=<id>), nur mit vorhandener ID. */}
              <strong>
                {ride.customer_id && ride.customer_name
                  ? <Link className="ride-person-link" to={`/users?detail=${encodeURIComponent(ride.customer_id)}`} title="Kundenakte öffnen">{ride.customer_name}</Link>
                  : (ride.customer_name || '—')}
              </strong>
              <p>{ride.customer_email || 'Keine E-Mail'}</p>
              <p>{ride.customer_phone || 'Keine Telefonnummer'}</p>
              <div className="ride-contact-meta">
                <span><Star size={12} /> {Number(ride.customer_rating || 5).toFixed(1)}</span>
                <span>{ride.payment_method === 'cash' ? <Banknote size={12} /> : <CreditCard size={12} />}
                  {ride.payment_method === 'cash' ? 'Barzahlung' : 'Kartenzahlung'}
                </span>
              </div>
            </article>

            <article className="ride-detail-card">
              <header><span><Car size={16} /></span><div><small>Ausführung</small><h3>Fahrer &amp; Flotte</h3></div></header>
              {ride.driver_name ? (
                <>
                  {/* AKTE-LINKS-FAHRT-DETAIL-001: Name öffnet die Fahrer-Akte (/drivers?detail=<id>), nur mit vorhandener ID. */}
                  <strong>
                    {ride.driver_id
                      ? <Link className="ride-person-link" to={`/drivers?detail=${encodeURIComponent(ride.driver_id)}`} title="Fahrer-Akte öffnen">{ride.driver_name}</Link>
                      : ride.driver_name}
                  </strong>
                  <p>{ride.driver_email || 'Keine E-Mail'}</p>
                  <p>{ride.driver_phone || 'Keine Telefonnummer'}</p>
                  <div className="ride-contact-meta">
                    {ride.company_name && <span><Building2 size={12} /> {ride.company_name}</span>}
                    {ride.vehicle_brand && <span><Car size={12} /> {ride.vehicle_brand} {ride.vehicle_model} · {ride.license_plate}</span>}
                  </div>
                </>
              ) : <div className="ride-unassigned">Noch kein Fahrer zugeordnet</div>}
            </article>
          </section>

          <section className="ride-route-card">
            <header>
              <div><span className="rides-overline">Fahrtroute</span><h3>{stops.length ? `Abholung · ${stops.length} ${stops.length === 1 ? 'Zwischenstopp' : 'Zwischenstopps'} · Ziel` : 'Abholung und Ziel'}</h3></div>
              <strong>{formatKm(ride.distance_km)} km · ca. {ride.duration_min ?? '—'} Min.</strong>
            </header>
            <div className={`ride-route-points${stops.length || unreadable ? ' has-stops' : ''}`}>
              <span className="is-pickup"><i /></span>
              <div><small>Abholung</small><strong>{ride.pickup_address || '—'}</strong></div>
              {!stops.length && !unreadable && <ChevronRight size={15} />}
              {stops.map((stop) => (
                <React.Fragment key={stop.number}>
                  <span className="is-stop" aria-hidden="true">{stop.number}</span>
                  <div data-testid="ride-intermediate-stop"><small>Zwischenstopp {stop.number}</small><strong>{stop.label}</strong></div>
                </React.Fragment>
              ))}
              {unreadable && <p className="ride-stops-unavailable">Zwischenstopps nicht lesbar</p>}
              <span className="is-dropoff"><i /></span>
              <div><small>Ziel</small><strong>{ride.dropoff_address || '—'}</strong></div>
            </div>
          </section>

          <section className="ride-detail-split">
            <article className="ride-ledger-card">
              <header><span><Wallet size={16} /></span><div><small>Abrechnung</small><h3>Finanzprojektion</h3></div></header>
              <DetailLine label={Number(ride.price_semantics_version) === 1 ? 'Zahlbetrag Kunde' : 'Fahrpreis (brutto)'} value={`${money(ride.price)} €`} strong />
              {Number.isFinite(Number(ride.stop_fee)) && Number(ride.stop_fee) > 0 && <DetailLine label="Stoppzuschlag (im Fahrpreis enthalten)" value={`${money(ride.stop_fee)} €`} />}
              {/* AKTIONEN-TRAEGT-VOOGO-001: Rabatt/Guthaben trägt VOOGO — der Kunde zahlt nur den Zahlbetrag. */}
              {ride.voogo_deductions > 0 && <DetailLine label="Rabatt/Guthaben (VOOGO trägt)" value={`−${money(ride.voogo_deductions)} €`} />}
              {ride.customer_due != null && ride.customer_due !== ride.price && <DetailLine label="Zahlbetrag Kunde" value={`${money(ride.customer_due)} €`} strong />}
              {/* Tax/net amounts are information; historical payout is the saved settlement result. */}
              <DetailLine label="VOOGO Provision (variabel)" value={ride.commission_amount == null ? 'nicht belegt' : `− ${money(ride.commission_amount)} €`} />
              <DetailLine label="Servicegebühr (fix)" value={ride.service_fee == null ? 'nicht belegt' : `− ${money(ride.service_fee)} €`} />
              {ride.voogo_total != null && <DetailLine label="VOOGO gesamt" value={`− ${money(ride.voogo_total)} €`} />}
              {ride.partner_payout != null
                ? <DetailLine label="Auszahlung Partner" value={`${money(ride.partner_payout)} €`} strong />
                : <DetailLine label="Auszahlung Partner" value={ride.status === 'completed' ? 'Auszahlung nicht belegt' : 'erst nach Abschluss'} />}
              <DetailLine label="darin enthaltene MwSt. 19 % (Partner-Umsatz)" value={`${money(ride.vat_amount)} €`} />
              <DetailLine label="Fahrpreis netto ohne MwSt. (Info)" value={`${money(ride.netto_amount)} €`} />
              {ride.wait_fee > 0 && <DetailLine label="Wartegebühr" value={`+${money(ride.wait_fee)} €`} />}
              {ride.cancellation_fee > 0 && <DetailLine label="Störungsgebühr" value={`${money(ride.cancellation_fee)} €`} />}
            </article>

            <article className="ride-timeline-card">
              <header><span><Clock3 size={16} /></span><div><small>Lebenszyklus</small><h3>Zeitlinie</h3></div></header>
              {(ride.scheduled_for || ride.reservation_planned_for) && (
                <DetailLine label="Geplante Abholung (Reservierung)" value={dateTime(ride.scheduled_for || ride.reservation_planned_for, true)} />
              )}
              {timelineEvents.map((entry) => (
                <DetailLine
                  key={entry.key}
                  label={timelineLabel(entry)}
                  value={dateTime(entry.occurred_at_ms ?? entry.occurred_at, true)}
                />
              ))}
              <DetailLine label="Distanz / Dauer" value={`${formatKm(ride.distance_km)} km · ca. ${ride.duration_min ?? '—'} Min.`} />
            </article>
          </section>

          {/* ADMIN-FAHRT-ZUWEISUNG-001: Sofortfahrt einem Fahrer zuweisen (searching) oder an einen anderen
              Fahrer übergeben (accepted|arrived). Reservierungen verwenden den eigenen bestehenden Vertrag. */}
          {ride.status === 'scheduled' && ride.scheduled_for && <ReservationReassign rideId={ride.id} onReassigned={onReassigned} />}
          {!ride.scheduled_for && ['searching', 'accepted', 'arrived'].includes(ride.status) && (
            <RideReassign rideId={ride.id} onReassigned={onReassigned} />
          )}

          <AdminRideCompletion key={JSON.stringify([ride.id, ride.status, ride.assignment_version, ride.route_revision])} ride={ride} onCompleted={onCompleted} />

          <RideLedgerSection rideId={ride.id} />

          {ride.status === 'cancelled' && (
            <section className="ride-cancelled-card">
              <header><Ban size={16} /><div><span className="rides-overline">Stornierung</span><h3>Abschlussgrund</h3></div></header>
              <DetailLine label="Storniert von" value={CANCELLED_BY_LABELS[ride.cancelled_by] || '—'} strong />
              <DetailLine label="Grund" value={cancelReason} strong />
              {driverReturns.length > 0 && (
                <DetailLine
                  label="Vorher vom Fahrer zurückgegeben"
                  value={`${driverReturns.length}× (${[...new Set(driverReturns.map((entry) => entry.driver_name || 'Fahrer'))].join(', ')})`}
                  strong
                />
              )}
            </section>
          )}

          {!['completed', 'cancelled'].includes(ride.status) && (
            <section className="ride-admin-action">
              <div>
                <span><ShieldAlert size={16} /></span>
                <div>
                  <strong>Administrative Stornierung</strong>
                  <small>Destruktive Aktion. Vor dem bestehenden Cancel-Endpunkt ist eine ausdrückliche Bestätigung erforderlich.</small>
                </div>
              </div>
              <button type="button" onClick={() => onCancel(ride.id)}>
                <Ban size={14} /> Fahrt stornieren (Admin)
              </button>
            </section>
          )}
        </div>
      </article>
    </div>
  );
}

export default function Rides() {
  const { summary: reservationSummary } = useReservationAlert();   // RESERVIERUNGSKETTE-001
  const { user: accountUser } = useAuth();
  const activeAccount = useRef(accountUser?.id || null);
  activeAccount.current = accountUser?.id || null;
  const detailRequestGeneration = useRef(0);
  const location = useLocation();
  const [rides, setRides] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // ⌘K-Palette: /rides?search=<q> übernimmt den Begriff als Startwert — der bestehende
  // Mount-Load unten nimmt ihn dann automatisch mit. Ohne Parameter bleibt alles wie bisher.
  const [search, setSearch] = useState(() => new URLSearchParams(location.search).get('search') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateField,setDateField]=useState('pickup');
  const [payment, setPayment] = useState('');
  const [rideType, setRideType] = useState('');
  const [limit, setLimit] = useState(25);
  const [writeoffView, setWriteoffView] = useState('active');
  const [selectedRide, setSelectedRide] = useState(null);
  const [error, setError] = useState(false);
  const listRequestGeneration = useRef(0);

  // overrideSearch: nur die ⌘K-Navigation reicht hier einen String durch (Klick-Handler
  // liefern ein Event-Objekt und fallen damit automatisch auf den State zurück).
  const load = useCallback((overrideSearch) => {
    const requestGeneration = ++listRequestGeneration.current;
    setLoading(true);
    setError(false);
    const effectiveSearch = typeof overrideSearch === 'string' ? overrideSearch : search;
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (effectiveSearch) params.set('search', effectiveSearch);
    if (dateFrom) params.set('from', dateFrom);
    if (dateTo) params.set('to', dateTo);
    params.set('date_field',dateField);
    if (payment) params.set('payment_method', payment);
    if (rideType) params.set('ride_type', rideType);
    if (writeoffView === 'only') params.set('writeoff', 'only');
    params.set('limit', String(limit));
    api.get(`/admin/rides?${params}`)
      .then((response) => {
        if (requestGeneration !== listRequestGeneration.current) return;
        const loadedRides = response.data.rides || [];
        setRides(loadedRides);
        setTotal(Number.isInteger(response.data.total) ? response.data.total : loadedRides.length);
        setLoading(false);
      })
      .catch(() => {
        if (requestGeneration !== listRequestGeneration.current) return;
        setError(true);
        setLoading(false);
      });
  }, [search, statusFilter, dateFrom, dateTo, dateField, payment, rideType, limit, writeoffView]);

  // Suche bleibt absichtlich Enter/Button-gesteuert.
  useEffect(() => { load(); }, [statusFilter, dateFrom, dateTo, dateField, payment, rideType, limit, writeoffView]);

  // ⌘K-Palette: spätere Navigationen mit ?search=<q> (auch von /rides aus) übernehmen den
  // Begriff und laden gezielt neu. Der Mount ist oben schon abgedeckt (Startwert + Mount-Load).
  const loadRef = useRef(null);
  loadRef.current = load;
  const skipFirstLocationRef = useRef(true);
  useEffect(() => {
    if (skipFirstLocationRef.current) { skipFirstLocationRef.current = false; return; }
    const q = new URLSearchParams(location.search).get('search');
    if (q === null) return;
    setSearch(q);
    loadRef.current(q);
  }, [location]);

  useEffect(() => {
    detailRequestGeneration.current += 1; setSelectedRide(null);
    return () => { detailRequestGeneration.current += 1; };
  }, [accountUser?.id]);

  const openDetail = useCallback(async (id) => {
    const generation = ++detailRequestGeneration.current;
    const account = accountUser?.id || null;
    try {
      const response = await api.get(`/admin/rides/${id}`);
      if (generation === detailRequestGeneration.current && account === activeAccount.current) setSelectedRide(rideDetailFromResponse(response.data));
    } catch {
      if (generation === detailRequestGeneration.current && account === activeAccount.current) toast.error('Fehler beim Laden');
    }
  }, [accountUser?.id]);

  // ADMIN-ABRECHNUNG-PRUEFANSICHT-001: /rides?ride=<id> öffnet das Fahrt-Detail direkt. Die
  // Prüfansicht der Abrechnung verlinkt jede Buchungszeile so auf ihre Fahrt — die Suche der
  // Fahrtenliste kennt keine Fahrt-ID. Ohne Parameter bleibt alles wie bisher.
  const requestedRideId = new URLSearchParams(location.search).get('ride');
  useEffect(() => {
    if (requestedRideId) openDetail(requestedRideId);
  }, [requestedRideId, openDetail]);

  const forceCancel = async (id) => {
    if (!window.confirm('Fahrt wirklich stornieren?')) return;
    try {
      await api.post(`/admin/rides/${id}/cancel`);
      toast.success('Fahrt storniert');
      setSelectedRide(null);
      load();
    } catch (requestError) {
      toast.error(requestError.response?.data?.error || 'Fehler');
    }
  };

  const counts = {};
  Object.keys(SL).forEach((status) => {
    counts[status] = rides.filter((ride) => ride.status === status).length;
  });

  const activeCount = rides.filter((ride) => ['searching', 'accepted', 'arrived', 'in_progress'].includes(ride.status)).length;
  const clearFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('');
    setPayment('');
    setRideType('');
  };
  const hasFilters = search || dateFrom || dateTo || statusFilter || payment || rideType;

  return (
    <div className="rides-page">
      {selectedRide && (
        <RideDetailModal
          ride={selectedRide}
          onClose={() => { detailRequestGeneration.current += 1; setSelectedRide(null); }}
          onCancel={forceCancel}
          onCompleted={() => { toast.success('Fahrt abgeschlossen.'); setSelectedRide(null); load(); }}
          onReassigned={() => { openDetail(selectedRide.id); load(); }}
        />
      )}

      <PageHeader
        title="Fahrten"
        description={writeoffView === 'only'
          ? 'Erhaltene Originalfahrten mit unveränderlichem Ausbuchungsnachweis.'
          : 'Systemweite Fahrtübersicht über Kunden, Fahrer, Partner und Zahlungswege.'}
        actions={
          <div className="rides-heading-actions">
            <span><Car size={13} /> {rides.length} geladen</span>
            <span><Clock3 size={13} /> {activeCount} aktiv</span>
            <button type="button" onClick={load} disabled={loading}>
              <RefreshCw size={14} /> {loading ? 'Lädt…' : 'Aktualisieren'}
            </button>
          </div>
        }
      />

      <section className="rides-view-switch" aria-label="Fahrtansicht">
        <button
          type="button"
          className={writeoffView === 'active' ? 'is-active' : ''}
          aria-pressed={writeoffView === 'active'}
          onClick={() => setWriteoffView('active')}
        >
          Reguläre Fahrten
        </button>
        <button
          type="button"
          className={writeoffView === 'only' ? 'is-active' : ''}
          aria-pressed={writeoffView === 'only'}
          onClick={() => setWriteoffView('only')}
        >
          Ausgebuchte Testfahrten
        </button>
        {/* RESERVIERUNG-KOMPLETT-001 (Owner 16.10.2026): Reservierungen haben einen eigenen Reiter. */}
        <Link className="rides-view-link" to="/reservierungen">Reservierungen{(reservationSummary.needs_admin ?? reservationSummary.attention) ? ` (${reservationSummary.needs_admin ?? reservationSummary.attention})` : ''} →</Link>
      </section>

      <EssenCompletionReviews onOpen={openDetail} reloadKey={rides} />

      <section className="rides-status-strip" aria-label="Fahrtstatus">
        <button
          type="button"
          className={!statusFilter ? 'is-active' : ''}
          aria-pressed={!statusFilter}
          onClick={() => setStatusFilter('')}
        >
          <span><Circle size={11} /> Alle</span><b>{rides.length}</b>
        </button>
        {Object.entries(SL).map(([status, label]) => (
          <button
            type="button"
            key={status}
            className={statusFilter === status ? 'is-active' : ''}
            style={{ '--ride-status': SC[status] }}
            aria-pressed={statusFilter === status}
            onClick={() => setStatusFilter(status === statusFilter ? '' : status)}
          >
            <span><i /> {label}</span><b>{counts[status] || 0}</b>
          </button>
        ))}
      </section>

      <section className="rides-filter-panel">
        <header>
          <div><span><Filter size={14} /></span><div><small>Serverfilter</small><strong>Fahrten eingrenzen</strong></div></div>
          <p>Von/Bis gelten je als Betriebstag 04:00–03:59 (Europe/Berlin). Es werden höchstens 25, 50 oder 100 aktuelle Datensätze geladen.</p>
        </header>
        <div className="rides-filter-grid">
          <label className="rides-search-field">
            <span>Suche</span>
            <div>
              <Search size={14} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && load()}
                placeholder="Fahrt-ID, Kunde, Fahrer, Partner oder Adresse"
              />
            </div>
          </label>
          <label>
            <span>Datumsbezug</span>
            <select value={dateField} onChange={event=>setDateField(event.target.value)}>
              <option value="pickup">Abholtermin</option>
              <option value="created">Erstellung</option>
            </select>
          </label>
          <label>
            <span>Von</span>
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label>
            <span>Bis</span>
            <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
          <label>
            <span>Zahlungsart</span>
            <select value={payment} onChange={(event) => setPayment(event.target.value)}>
              <option value="">Alle Zahlungen</option>
              <option value="cash">Bar</option>
              <option value="card">Karte</option>
            </select>
          </label>
          <label>
            <span>Fahrzeugklasse</span>
            <select value={rideType} onChange={(event) => setRideType(event.target.value)}>
              <option value="">Alle Klassen</option>
              <option value="standard">{TL.standard}</option>
              <option value="comfort">{TL.comfort}</option>
              <option value="premium">{TL.premium}</option>
              <option value="xl">{TL.xl}</option>
              <option value="max">{TL.max}</option>
              <option value="taxi">{TL.taxi}</option>
            </select>
          </label>
          <label className="rides-limit-field">
            <span>Anzahl</span>
            <select value={limit} onChange={(event) => setLimit(Number(event.target.value))} title="Anzahl angezeigter Fahrten">
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <div className="rides-filter-actions">
            <button type="button" className="is-search" onClick={load}><Search size={13} /> Suchen</button>
            {hasFilters && (
              <button type="button" className="is-clear" onClick={clearFilters} aria-label="Fahrtenfilter zurücksetzen">
                <X size={13} /> Zurücksetzen
              </button>
            )}
          </div>
        </div>
      </section>

      {error && <ErrorBox text="Fahrten konnten nicht geladen werden." onRetry={load} style={{ marginBottom: 12 }} />}

      <section className="rides-table-card">
        <header>
          <div>
            <span className="rides-overline">{writeoffView === 'only' ? 'Getrennte Nachweisansicht' : 'Systemweite Liste'}</span>
            <h2>{writeoffView === 'only' ? 'Ausgebuchte Testfahrten' : 'Geladene Fahrten'}</h2>
          </div>
          <span><CalendarDays size={13} /> {total} insgesamt · Neueste zuerst</span>
        </header>
        <div className="rides-table-scroll">
          {loading ? (
            <div className="rides-loading"><Skeleton h={170} r={10} /></div>
          ) : (
            <table className="rides-table">
              <thead>
                <tr>
                  {['ID', 'Typ', 'Kunde', 'Fahrer', 'Partner', 'Route', 'Preis', 'Zahlung', 'Status', 'Datum', ...(writeoffView === 'only' ? ['Ausbuchung'] : [])].map((heading) => (
                    <th key={heading}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rides.map((ride) => (
                  <tr
                    key={ride.id}
                    onClick={() => openDetail(ride.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') openDetail(ride.id);
                    }}
                    tabIndex={0}
                  >
                    <td><span className="rides-id">{ride.id.slice(0, 8)}…</span></td>
                    <td><TypePill type={ride.ride_type} /></td>
                    <td><strong>{ride.customer_name}</strong></td>
                    <td><span>{ride.driver_name || '—'}</span></td>
                    <td><span>{ride.company_name || '—'}</span></td>
                    <td>
                      <div className="rides-route-cell">
                        <span><MapPin size={11} /> {ride.pickup_address}</span>
                        <span><Circle size={11} /> {ride.dropoff_address}</span>
                      </div>
                    </td>
                    <td>
                      {ride.price_in_review ? <>
                        <strong className="rides-price">Preis in Prüfung</strong>
                        <div className="rides-price-due" title="Beendet ohne Abrechnung: nichts gebucht, nichts eingezogen">Schätzung {money(ride.price)} € · nicht abgerechnet</div>
                      </> : ride.price_review_released ? <strong className="rides-price">Ohne Abrechnung</strong>
                        : <strong className="rides-price">{money(ride.price)} €</strong>}
                      {!ride.price_in_review && !ride.price_review_released && ride.customer_due != null && ride.customer_due !== ride.price && (
                        <div className="rides-price-due" title="Zahlbetrag des Kunden (Rabatt/Guthaben trägt VOOGO)">Kunde zahlt {money(ride.customer_due)} €</div>
                      )}
                    </td>
                    <td>
                      <span className="rides-payment" title={ride.payment_method === 'cash' ? 'Barzahlung' : 'Kartenzahlung'}>
                        {ride.payment_method === 'cash' ? <Banknote size={13} /> : <CreditCard size={13} />}
                        {ride.payment_method === 'cash' ? 'Bar' : 'Karte'}
                      </span>
                    </td>
                    <td><StatusPill status={ride.dispatch_reviews?.some(review => review.state === 'open') ? 'admin_review' : (ride.price_in_review ? 'price_review' : ride.price_review_released ? 'price_released' : ride.status)} /></td>
                    <td><time>{dateTime(dateField==='pickup'?ride.scheduled_for||ride.created_at:ride.created_at)}</time>{ride.scheduled_for&&<small className="rides-reservation-date">{dateField==='pickup'?'Abholtermin':'Abholung: '+dateTime(ride.scheduled_for,true)}</small>}</td>
                    {writeoffView === 'only' && (
                      <td className="rides-writeoff-cell">
                        <strong>{ride.test_writeoff_reason || 'Ohne Grund'}</strong>
                        <span>{dateTime(ride.test_writeoff_at, true)}</span>
                        <span>Akteur: {ride.test_writeoff_actor_id || '—'}</span>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && rides.length === 0 && (
                  <tr>
                    <td colSpan={writeoffView === 'only' ? 11 : 10}>
                      <Empty icon={Car} title="Keine Fahrten gefunden" hint="Filter anpassen oder Zeitraum erweitern." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
